import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { cashScopeKey } from "@/hooks/use-branch-cash";
import { staffCall } from "@/hooks/use-staff";
import { fetchAPI } from "@/lib/api";
import type { CertificateSigningData } from "./staff-step22";
import type { CertificateFields } from "./staff-step17";
import type { CertificateSignature } from "./staff-step22";

export function verifyStaffCertificatePrint(id:string):Promise<{awardId:string;certificateSnapshot:CertificateFields|null;signatures:CertificateSignature[];certificateStatus:"ready"}>{
 return staffCall("GET",`/api/staff/bonus-awards/${encodeURIComponent(id)}/certificate`);
}

export function useStaffCertificateSigning(enabled=true){
 const {currentWorkspace,user,roles}=useAuth();
 return useQuery<CertificateSigningData>({
  queryKey:["branch-cash","staff-certificate-signing",...cashScopeKey(currentWorkspace?.id,user,roles?.find(r=>r.id===user?.roleId)?.permissions.map(p=>p.id))],
  queryFn:()=>staffCall("GET","/api/staff/certificate-signing"),enabled:enabled&&!!currentWorkspace?.id&&!!user,
 });
}
export function useCertificateSignatureAction(){
 const cache=useQueryClient();
 return useMutation<{id:string;awardId:string;status:string},Error,{id:string;action:"sign"|"decline"|"remind";idempotencyKey:string;reason?:string}>({
  mutationFn:({id,action,...body})=>staffCall("POST",`/api/staff/certificate-signatures/${encodeURIComponent(id)}/${action}`,body),
  onSuccess:async()=>{await Promise.all(["staff-certificate-signing","staff-certificates","staff-bonuses","staff-pay","staff-alerts"].map(key=>cache.invalidateQueries({queryKey:["branch-cash",key]})));await cache.invalidateQueries({queryKey:["/api/staff/bootstrap"]})},
 });
}
export function useCertificateLogoUpload(){
 const {currentWorkspace}=useAuth();
 return useMutation<{key:string},Error,File>({mutationFn:file=>{
  if(!["image/png","image/jpeg","image/webp"].includes(file.type))throw new Error("Use a PNG, JPG or WebP image. / استخدم صورة PNG أو JPG أو WebP.");
  if(file.size>2_000_000)throw new Error("That image is over 2 MB. Pick a smaller one. / الصورة أكبر من 2 ميجابايت. اختر صورة أصغر.");
  return fetchAPI("/staff/certificate-logos",{method:"POST",body:file,headers:{"Content-Type":file.type,"X-File-Name":encodeURIComponent(file.name),"x-workspace-id":currentWorkspace?.id||""}});
 }});
}
export function useCertificateLogo(key?:string|null){
 const {currentWorkspace,user}=useAuth(),[state,setState]=useState<{key:string;url?:string;error?:boolean}>(),[retry,setRetry]=useState(0);
 useEffect(()=>{
  if(!key||!currentWorkspace?.id||!user?.id)return;
  const controller=new AbortController();let objectUrl="";
  setState({key});
  void fetch(`/api/staff/certificate-logos?key=${encodeURIComponent(key)}`,{credentials:"include",headers:{"x-workspace-id":currentWorkspace.id},signal:controller.signal})
   .then(async response=>{if(!response.ok)throw Error("Logo unavailable");const blob=await response.blob();if(!controller.signal.aborted){objectUrl=URL.createObjectURL(blob);setState({key,url:objectUrl})}})
   .catch(()=>{if(!controller.signal.aborted)setState({key,error:true})});
  return()=>{controller.abort();if(objectUrl)URL.revokeObjectURL(objectUrl)};
 },[key,currentWorkspace?.id,user?.id,retry]);
 return {url:key&&state?.key===key?state.url:undefined,error:!!key&&state?.key===key&&!!state.error,retry:()=>setRetry(n=>n+1)};
}
